import React, { useMemo } from "react"

import type {
  AudioFilterConfig,
  AudioFilterEntryConfig,
} from "@/types/metrics/metadata"

import { combineWithJoins, type CombineOps } from "../../filter-list"

type SegmentKind = "agent" | "persona" | "music"

interface DemoSegment {
  kind: SegmentKind
  start: number
  end: number
}

type Range = [number, number]

const TOTAL_DURATION_SECONDS = 129
const CELL_WIDTH_SECONDS = 3
const CELL_COUNT = TOTAL_DURATION_SECONDS / CELL_WIDTH_SECONDS

// Demo timeline modeled on a realistic ~2 minute call (43 cells at 3s each):
//   19 agent + 19 persona speech turns (2 of each form the overlap pairs)
//   3 music regions (12-15, 45-48, 90-93)
//   2 agent-persona overlap windows (34-36, 82-84)
//   2 silence gaps (60-66, 108-114)
// Each cell's midpoint lands clearly inside one region so every filter type
// shows distinct highlighting without aliasing between cells.
const DEMO_SEGMENTS: DemoSegment[] = [
  { kind: "agent", start: 0, end: 3 },
  { kind: "persona", start: 3, end: 6 },
  { kind: "agent", start: 6, end: 9 },
  { kind: "persona", start: 9, end: 12 },
  { kind: "music", start: 12, end: 15 },
  { kind: "agent", start: 15, end: 18 },
  { kind: "persona", start: 18, end: 21 },
  { kind: "agent", start: 21, end: 24 },
  { kind: "persona", start: 24, end: 27 },
  { kind: "agent", start: 27, end: 30 },
  { kind: "persona", start: 30, end: 33 },
  { kind: "agent", start: 33, end: 36 },
  { kind: "persona", start: 34, end: 36 },
  { kind: "persona", start: 36, end: 39 },
  { kind: "agent", start: 39, end: 42 },
  { kind: "persona", start: 42, end: 45 },
  { kind: "music", start: 45, end: 48 },
  { kind: "agent", start: 48, end: 51 },
  { kind: "persona", start: 51, end: 54 },
  { kind: "agent", start: 54, end: 57 },
  { kind: "persona", start: 57, end: 60 },
  { kind: "agent", start: 66, end: 69 },
  { kind: "persona", start: 69, end: 72 },
  { kind: "agent", start: 72, end: 75 },
  { kind: "persona", start: 75, end: 78 },
  { kind: "agent", start: 78, end: 81 },
  { kind: "persona", start: 81, end: 84 },
  { kind: "agent", start: 82, end: 84 },
  { kind: "agent", start: 84, end: 87 },
  { kind: "persona", start: 87, end: 90 },
  { kind: "music", start: 90, end: 93 },
  { kind: "agent", start: 93, end: 96 },
  { kind: "persona", start: 96, end: 99 },
  { kind: "agent", start: 99, end: 102 },
  { kind: "persona", start: 102, end: 105 },
  { kind: "agent", start: 105, end: 108 },
  { kind: "persona", start: 114, end: 117 },
  { kind: "agent", start: 117, end: 120 },
  { kind: "persona", start: 120, end: 123 },
  { kind: "agent", start: 123, end: 126 },
  { kind: "persona", start: 126, end: 129 },
]

const SEGMENT_STYLES: Record<
  SegmentKind | "silence" | "overlap",
  { bg: string; bgIncluded: string; label: string }
> = {
  agent: {
    bg: "bg-green-100 dark:bg-green-950",
    bgIncluded: "bg-green-300 dark:bg-green-700",
    label: "Agent",
  },
  persona: {
    bg: "bg-blue-100 dark:bg-blue-950",
    bgIncluded: "bg-blue-300 dark:bg-blue-700",
    label: "Persona",
  },
  music: {
    bg: "bg-purple-100 dark:bg-purple-950",
    bgIncluded: "bg-purple-300 dark:bg-purple-700",
    label: "Music",
  },
  silence: {
    bg: "bg-surface-2",
    bgIncluded: "bg-coval-text-muted",
    label: "Silence",
  },
  overlap: {
    bg: "bg-amber-100 dark:bg-amber-950",
    bgIncluded: "bg-amber-300 dark:bg-amber-700",
    label: "Overlap",
  },
}

function mergeRanges(ranges: Range[]): Range[] {
  if (ranges.length === 0) {
    return []
  }
  const sorted = [...ranges]
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0])
  if (sorted.length === 0) {
    return []
  }
  const merged: Range[] = [sorted[0]]
  for (let i = 1; i < sorted.length; i++) {
    const [start, end] = sorted[i]
    const last = merged[merged.length - 1]
    if (start <= last[1]) {
      last[1] = Math.max(last[1], end)
    } else {
      merged.push([start, end])
    }
  }
  return merged
}

function intersectRanges(a: Range[], b: Range[]): Range[] {
  const result: Range[] = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    const start = Math.max(a[i][0], b[j][0])
    const end = Math.min(a[i][1], b[j][1])
    if (end > start) {
      result.push([start, end])
    }
    if (a[i][1] < b[j][1]) {
      i++
    } else {
      j++
    }
  }
  return result
}

function subtractRanges(base: Range[], remove: Range[]): Range[] {
  if (remove.length === 0) {
    return [...base]
  }
  const result: Range[] = []
  for (const [baseStart, baseEnd] of base) {
    let cursor = baseStart
    for (const [removeStart, removeEnd] of remove) {
      if (removeEnd <= cursor || removeStart >= baseEnd) {
        continue
      }
      if (removeStart > cursor) {
        result.push([cursor, Math.min(removeStart, baseEnd)])
      }
      cursor = Math.max(cursor, removeEnd)
      if (cursor >= baseEnd) {
        break
      }
    }
    if (cursor < baseEnd) {
      result.push([cursor, baseEnd])
    }
  }
  return mergeRanges(result)
}

function speakingSegmentsByKind(kinds: SegmentKind[]): Range[] {
  return mergeRanges(
    DEMO_SEGMENTS.filter((s) => kinds.includes(s.kind)).map(
      (s) => [s.start, s.end] as Range,
    ),
  )
}

function turnsBySpeaker(
  speaker: "agent" | "persona" | "music" | "any",
): DemoSegment[] {
  if (speaker === "any") {
    return DEMO_SEGMENTS
  }
  return DEMO_SEGMENTS.filter((s) => s.kind === speaker)
}

function resolveFilter(filter: AudioFilterEntryConfig): Range[] {
  switch (filter.type) {
    case "speaker":
      return speakingSegmentsByKind(filter.speakers)
    case "silence": {
      const speech = speakingSegmentsByKind(["agent", "persona"])
      const threshold = filter.min_gap_seconds ?? 0.5
      const gaps: Range[] = []
      let cursor = 0
      for (const [start, end] of speech) {
        if (start - cursor >= threshold) {
          gaps.push([cursor, start])
        }
        cursor = Math.max(cursor, end)
      }
      if (TOTAL_DURATION_SECONDS - cursor >= threshold) {
        gaps.push([cursor, TOTAL_DURATION_SECONDS])
      }
      return gaps
    }
    case "overlap":
      return intersectRanges(
        speakingSegmentsByKind(["agent"]),
        speakingSegmentsByKind(["persona"]),
      )
    case "music":
      return speakingSegmentsByKind(["music"])
    case "time_range": {
      const count = Math.min(filter.count, TOTAL_DURATION_SECONDS)
      if (count <= 0) {
        return []
      }
      if (filter.mode === "first_n_seconds") {
        return [[0, count]]
      }
      if (filter.mode === "last_n_seconds") {
        return [[TOTAL_DURATION_SECONDS - count, TOTAL_DURATION_SECONDS]]
      }
      const start = Math.max(0, (TOTAL_DURATION_SECONDS - count) / 2)
      return [[start, start + count]]
    }
    case "turn_range": {
      const turns = turnsBySpeaker(filter.speaker ?? "any")
      const selected =
        filter.mode === "first_n"
          ? turns.slice(0, filter.count)
          : turns.slice(-filter.count)
      return mergeRanges(selected.map((s) => [s.start, s.end] as Range))
    }
  }
}

const RANGE_OPS: CombineOps<Range[]> = {
  intersect: intersectRanges,
  union: (left, right) => mergeRanges([...left, ...right]),
  empty: [],
}

function composeRanges(
  entries: AudioFilterEntryConfig[],
  joins: ("or" | "and")[],
): Range[] {
  return combineWithJoins(
    entries,
    joins,
    (entry) => mergeRanges(resolveFilter(entry)),
    RANGE_OPS,
  )
}

function applyFilters(config: AudioFilterConfig): Range[] {
  const includes = config.include ?? []
  const excludes = config.exclude ?? []
  const includeJoins = config.include_joins ?? []
  const excludeJoins = config.exclude_joins ?? []

  const included: Range[] =
    includes.length === 0
      ? [[0, TOTAL_DURATION_SECONDS]]
      : composeRanges(includes, includeJoins)
  if (included.length === 0) {
    return []
  }
  const excluded = composeRanges(excludes, excludeJoins)
  return subtractRanges(included, excluded)
}

function isTimestampIncluded(timestamp: number, ranges: Range[]): boolean {
  for (const [start, end] of ranges) {
    if (timestamp >= start && timestamp < end) {
      return true
    }
  }
  return false
}

function kindAt(timestamp: number): SegmentKind | "silence" | "overlap" {
  const agent = DEMO_SEGMENTS.some(
    (s) => s.kind === "agent" && timestamp >= s.start && timestamp < s.end,
  )
  const persona = DEMO_SEGMENTS.some(
    (s) => s.kind === "persona" && timestamp >= s.start && timestamp < s.end,
  )
  const music = DEMO_SEGMENTS.some(
    (s) => s.kind === "music" && timestamp >= s.start && timestamp < s.end,
  )
  if (agent && persona) {
    return "overlap"
  }
  if (agent) {
    return "agent"
  }
  if (persona) {
    return "persona"
  }
  if (music) {
    return "music"
  }
  return "silence"
}

interface AudioPreviewProps {
  config: AudioFilterConfig
}

export const AudioPreview: React.FC<AudioPreviewProps> = ({ config }) => {
  const hasFilters =
    (config.include && config.include.length > 0) ||
    (config.exclude && config.exclude.length > 0)

  const includedRanges = useMemo(() => applyFilters(config), [config])

  const includedSeconds = useMemo(
    () => includedRanges.reduce((sum, [s, e]) => sum + (e - s), 0),
    [includedRanges],
  )

  if (!hasFilters) {
    return null
  }

  const cells = Array.from({ length: CELL_COUNT }, (_, i) => {
    const midpoint = (i + 0.5) * CELL_WIDTH_SECONDS
    const kind = kindAt(midpoint)
    const included = isTimestampIncluded(midpoint, includedRanges)
    return { index: i, kind, included }
  })

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-coval-text-muted">
          Preview: {includedSeconds.toFixed(1)}s of {TOTAL_DURATION_SECONDS}s
          included
        </span>
        <div className="flex items-center gap-3 text-xs text-coval-text-muted">
          {(["agent", "persona", "music", "overlap", "silence"] as const).map(
            (kind) => (
              <span key={kind} className="flex items-center gap-1">
                <span
                  className={`w-3 h-3 rounded ${SEGMENT_STYLES[kind].bgIncluded}`}
                />
                {SEGMENT_STYLES[kind].label}
              </span>
            ),
          )}
        </div>
      </div>
      <div className="flex gap-0.5 w-full">
        {cells.map((cell) => {
          const styles = SEGMENT_STYLES[cell.kind]
          return (
            <div
              key={cell.index}
              className={`
                flex-1 h-6 rounded-sm transition-colors duration-200
                ${cell.included ? styles.bgIncluded : styles.bg}
              `}
              title={`${styles.label} @ ${(cell.index * CELL_WIDTH_SECONDS).toFixed(1)}s — ${cell.included ? "included" : "excluded"}`}
            />
          )
        })}
      </div>
    </div>
  )
}
